import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react"

import { debounce } from "lodash"

import { Collection, Model } from "wealthmode-frontend/backend"

import RawDataTable from "@/components/RawDataTable"
import { useLoadOnFocus } from "@/lib/useLoadOnFocus"

export type DataTableHeader = {
  id: string
  center?: boolean
  title?: string
  /** Fit content width (e.g. ID); other columns absorb remaining space first. */
  shrink?: boolean
  /** Buttons/links: no ellipsis; column fits content. */
  noTruncate?: boolean
  /** Allow multi-line when space is still insufficient. */
  wrap?: boolean
}

export type RawDataTablePagination = {
  showPrev: boolean
  showNext: boolean
  onPrev: () => void
  onNext: () => void
  onRefresh: () => void
  refreshDisabled?: boolean
}

export type RawDataTableSearch = {
  mode: "remote" | "local"
  value: string
  onChangeText: (text: string) => void
}

export type DataTableSearch<T extends Model<any> = Model<any>> =
  { mode: "remote" } | { mode: "local"; getCellSearchText?: (model: T, field: string) => string }

export type DataTableFilters = {
  /** Labels of the filters currently applied; empty when none are. */
  active: string[]
  /** Controls shown in the filter dialog. The page owns their state and feeds it into its loader. */
  controls: ReactNode
}

export type DataTableLoader<T extends Model<any> = Model<any>> = () => Promise<Collection<T> | undefined>

const REMOTE_SEARCH_DEBOUNCE_MS = 500

function reactNodeSearchText(node: unknown): string {
  if (node == null || node === false) {
    return ""
  }

  if (typeof node === "string" || typeof node === "number" || typeof node === "boolean") {
    return String(node)
  }

  if (Array.isArray(node)) {
    return node
      .map(reactNodeSearchText)
      .filter((part) => part !== "")
      .join(" ")
  }

  if (!React.isValidElement(node)) {
    return ""
  }

  const props = node.props as { children?: unknown }
  return reactNodeSearchText(props.children)
}

export function cellSearchText(
  render: (model: Model<any>, field: string) => unknown,
  model: Model<any>,
  field: string,
  getCellSearchText?: (model: Model<any>, field: string) => string,
): string {
  if (getCellSearchText) {
    return getCellSearchText(model, field)
  }

  const content = render(model, field)
  if (content == null || content === "") {
    return ""
  }

  if (typeof content === "string" || typeof content === "number" || typeof content === "boolean") {
    return String(content)
  }

  const fromNode = reactNodeSearchText(content)
  if (fromNode !== "") {
    return fromNode
  }

  const raw = model.get(field)
  if (raw != null && raw !== "") {
    return String(raw)
  }

  return ""
}

export function modelMatchesLocalFilter(
  model: Model<any>,
  fields: string[],
  filter: string,
  render: (model: Model<any>, field: string) => unknown,
  getCellSearchText?: (model: Model<any>, field: string) => string,
): boolean {
  const trimmed = filter.trim()
  if (trimmed === "") {
    return true
  }

  const needle = trimmed.toLowerCase()

  return fields.some((field) => cellSearchText(render, model, field, getCellSearchText).toLowerCase().includes(needle))
}

export default function DataTable<T extends Model<any> = Model<any>>({
  load,
  title,
  header,
  render,
  search,
  refreshKey,
  filters,
}: {
  load: DataTableLoader<T>
  title: string
  header: Array<DataTableHeader>
  render: (model: T, field: string) => any
  search?: DataTableSearch<T>
  /** When changed, reloads table data (e.g. after creating a row). */
  refreshKey?: number
  filters?: DataTableFilters
}) {
  const effectiveSearch = search ?? { mode: "local" as const }

  const [loading, setLoading] = useState(true)
  const [pending, setPending] = useState(false)
  const [root, setRoot] = useState<Collection<T> | undefined>()
  const [list, setList] = useState<Collection<T> | undefined>()
  const [searchText, setSearchText] = useState("")
  const rootCollection = useRef<Collection<T> | undefined>(root)

  const reload = useCallback(async () => {
    setLoading(true)

    try {
      const collection = await load()
      rootCollection.current = collection
      setRoot(collection)
      setList(collection)
    } catch (err) {
      console.error("Error loading table data:", err)
      rootCollection.current = undefined
      setRoot(undefined)
      setList(undefined)
      throw err
    } finally {
      setLoading(false)
    }
  }, [load])

  useLoadOnFocus(reload)

  useEffect(() => {
    if (refreshKey === undefined) {
      return
    }

    void reload()
  }, [refreshKey, reload])

  const runRemoteSearch = useCallback((q: string) => {
    const root = rootCollection.current
    if (!root) {
      return
    }

    setPending(true)

    const trimmed = q.trim()
    const fetchPromise =
      trimmed === "" ? root.fetch({ reload: true }) : root.fetch({ query: new URLSearchParams({ q: trimmed }) })

    fetchPromise
      .then(() => {
        setList(root)
      })
      .finally(() => {
        setPending(false)
      })
  }, [])

  const debouncedRemoteSearch = useCallback(debounce(runRemoteSearch, REMOTE_SEARCH_DEBOUNCE_MS), [runRemoteSearch])

  useEffect(() => {
    return () => {
      debouncedRemoteSearch.cancel()
    }
  }, [debouncedRemoteSearch])

  useEffect(() => {
    if (loading) {
      return
    }

    if (!list) {
      setPending(false)
      return
    }

    if (list === root) {
      setPending(false)
      return
    }

    setPending(true)
    list.fetch().then(() => {
      setPending(false)
    })
  }, [list, loading, root])

  function onSearchChange(text: string) {
    setSearchText(text)

    if (effectiveSearch.mode === "local") {
      return
    }

    setPending(true)
    debouncedRemoteSearch(text)
  }

  function onLink(rel: string) {
    setPending(true)
    setList(list?.link(rel)?.resolve() as Collection<T>)
  }

  const refreshCurrentPage = useCallback(async () => {
    const current = list
    if (!current) {
      return
    }

    setPending(true)

    try {
      if (current === root) {
        const trimmed = searchText.trim()
        if (effectiveSearch.mode === "remote" && trimmed !== "") {
          await current.fetch({ query: new URLSearchParams({ q: trimmed }), reload: true })
        } else {
          await current.fetch({ reload: true })
        }

        setList(current)
      } else {
        await current.fetch({ reload: true })
      }
    } finally {
      setPending(false)
    }
  }, [list, root, searchText, effectiveSearch.mode])

  const columnIds = header.map((hdr) => hdr.id)
  const rows = (list?.models ?? []).filter((model) => {
    if (effectiveSearch.mode !== "local" || searchText.trim() === "") {
      return true
    }

    return modelMatchesLocalFilter(
      model,
      columnIds,
      searchText,
      render as (model: Model<any>, field: string) => unknown,
      effectiveSearch.mode === "local"
        ? (effectiveSearch.getCellSearchText as ((model: Model<any>, field: string) => string) | undefined)
        : undefined,
    )
  })

  return (
    <RawDataTable<T>
      loading={loading || pending}
      rows={rows}
      title={title}
      header={header}
      render={render}
      pagination={{
        showPrev: !loading && !pending && list?.link("prev") != null,
        showNext: !loading && !pending && list?.link("next") != null,
        onPrev: () => onLink("prev"),
        onNext: () => onLink("next"),
        onRefresh: () => {
          void refreshCurrentPage()
        },
        refreshDisabled: loading || pending || !list,
      }}
      search={{
        mode: effectiveSearch.mode,
        value: searchText,
        onChangeText: onSearchChange,
      }}
      filters={filters}
    />
  )
}
